/**
 * Field format validators (piece 2: Field format validators). United States
 * rules only: every validator takes a countryCode and dispatches on it, one US
 * branch and a permissive default for every other country. The US rule is
 * never applied as a fallback for an unknown country. Hard block only where a
 * checksum or a shape gives real certainty; warn everywhere else.
 */

import { PhoneNumberError, toE164 } from './records/phone';

export type ValidationSeverity = 'block' | 'warn';

export type ValidationOutcome =
  | { readonly ok: true; readonly normalized: string }
  | { readonly ok: false; readonly severity: ValidationSeverity; readonly message: string; readonly normalized: string };

/** The stable key a ValidationWarningAcknowledgement row names for each validator. */
export const validatorKeys = {
  taxId: 'taxId',
  routingNumber: 'routingNumber',
  phone: 'phone',
  postalCode: 'postalCode',
  region: 'region',
  email: 'email',
} as const;
export type ValidatorKey = (typeof validatorKeys)[keyof typeof validatorKeys];

const US = 'US';
/** Only the digits of a string (T678: the one copy; the formatted input and the vendor tax checks read it from here). */
export const digitsOf = (value: string): string => value.replace(/\D/g, '');
const pass = (normalized: string): ValidationOutcome => ({ ok: true, normalized });
const block = (message: string, normalized: string): ValidationOutcome => ({ ok: false, severity: 'block', message, normalized });
const warn = (message: string, normalized: string): ValidationOutcome => ({ ok: false, severity: 'warn', message, normalized });

/** The two-digit prefixes the IRS issues; an EIN outside them is not an EIN. */
const EIN_PREFIXES = new Set([
  '01', '02', '03', '04', '05', '06', '10', '11', '12', '13', '14', '15', '16', '20', '21', '22', '23', '24', '25', '26', '27',
  '30', '31', '32', '33', '34', '35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47', '48',
  '50', '51', '52', '53', '54', '55', '56', '57', '58', '59', '60', '61', '62', '63', '64', '65', '66', '67', '68',
  '71', '72', '73', '74', '75', '76', '77', '80', '81', '82', '83', '84', '85', '86', '87', '88', '90', '91', '92', '93', '94', '95', '98', '99',
]);

/** Tax ID: a 9-digit EIN with a valid prefix for US, a hard block; accepted as entered elsewhere. */
export function validateTaxId(value: string, countryCode: string): ValidationOutcome {
  const trimmed = value.trim();
  if (countryCode !== US) return pass(trimmed);
  const digits = digitsOf(trimmed);
  if (digits.length !== 9) return block('An employer identification number has 9 digits.', digits);
  if (!EIN_PREFIXES.has(digits.slice(0, 2))) return block('The first two digits are not an IRS prefix.', digits);
  return pass(`${digits.slice(0, 2)}-${digits.slice(2)}`);
}

/** Bank routing: a 9-digit ABA number with its checksum for US, a hard block; accepted as entered elsewhere. */
export function validateRoutingNumber(value: string, countryCode: string): ValidationOutcome {
  const trimmed = value.trim();
  if (countryCode !== US) return pass(trimmed);
  const digits = digitsOf(trimmed);
  if (digits.length !== 9) return block('A routing number has 9 digits.', digits);
  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  const sum = [...digits].reduce((acc, d, i) => acc + Number(d) * weights[i]!, 0);
  if (sum % 10 !== 0) return block('The routing number fails its checksum.', digits);
  return pass(digits);
}

/**
 * Phone: the number as the store would keep it, E.164 with its country code (T2170).
 * 🔨 One rule for the mask's warning and for storage: it normalises through `toE164`, so
 * `020 7946 0958` in GB reads `+442079460958` and a United States number needs its ten
 * digits. A number the table cannot place, or one outside its country's length, is a warning
 * naming the problem, never a guess. A warning.
 */
export function validatePhone(value: string, countryCode: string): ValidationOutcome {
  const digits = digitsOf(value);
  try {
    return pass(toE164(value, countryCode));
  } catch (error) {
    if (error instanceof PhoneNumberError) return warn(error.message, digits);
    throw error;
  }
}

/** Postal code: 5 or 9 digits for US, a warning; accepted and not required elsewhere. */
export function validatePostalCode(value: string, countryCode: string): ValidationOutcome {
  const trimmed = value.trim();
  if (countryCode !== US) return pass(trimmed);
  const digits = digitsOf(trimmed);
  if (digits.length === 5) return pass(digits);
  if (digits.length === 9) return pass(`${digits.slice(0, 5)}-${digits.slice(5)}`);
  return warn('A US ZIP code has 5 or 9 digits.', digits);
}

/** The fifty states and the District of Columbia. */
export const US_REGIONS: readonly string[] = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD',
  'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

/** Region: a 2-letter state chosen from the list and required for US; free text and optional elsewhere. */
export function validateRegion(value: string | null | undefined, countryCode: string): ValidationOutcome {
  const trimmed = (value ?? '').trim();
  if (countryCode !== US) return pass(trimmed);
  const upper = trimmed.toUpperCase();
  if (upper.length === 0) return block('A US address names its state.', upper);
  if (!US_REGIONS.includes(upper)) return block('The state is chosen from the list.', upper);
  return pass(upper);
}

/**
 * 🔒 Whether text is one email address (T2110; decision 2833): one mailbox,
 * `name@domain.tld`, with no whitespace and none of `<>(),;:"` in it. The one
 * rule: the Person form, Compose, a document's send, a mailbox grant, the
 * unsubscribe target, a test delivery and a signed copy all read it. 💰 Six rules
 * stood until 2026-10-10, so `bob,smith@example.com` saved on a Person that
 * Compose then refused, and `a@b` passed a signed copy and nothing else.
 */
export function isEmailAddress(value: string): boolean {
  return /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/.test(value.trim());
}

/** Email: shape only, the one universal rule, `isEmailAddress`; a hard block. */
export function validateEmail(value: string, _countryCode: string): ValidationOutcome {
  const trimmed = value.trim();
  if (!isEmailAddress(trimmed)) return block('That is not the shape of an email address.', trimmed);
  return pass(trimmed.toLowerCase());
}

/** The validators by key, so a caller resolves one from a stored validatorKey. */
export const validators: Record<ValidatorKey, (value: string, countryCode: string) => ValidationOutcome> = {
  taxId: validateTaxId,
  routingNumber: validateRoutingNumber,
  phone: validatePhone,
  postalCode: validatePostalCode,
  region: (value, countryCode) => validateRegion(value, countryCode),
  email: validateEmail,
};
